import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import axios from 'axios';
import { API_BASE } from '../../lib/utils';
import { toast } from 'sonner';
import { ArrowRight, CheckCircle, ThumbsUp, ThumbsDown, Zap } from 'lucide-react';

const DIMENSION_COLORS = {
  conscientiousness: '#0071e3',
  openness: '#34C759',
  stability: '#FF9500',
  agreeableness: '#AF52DE',
  extraversion: '#FF2D55',
};

const PersonalityProfile = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [completed, setCompleted] = useState(false);
  const [profile, setProfile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [quickRunning, setQuickRunning] = useState(false);
  const [imgLoaded, setImgLoaded] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [questionsRes, statusRes] = await Promise.all([
        axios.get(`${API_BASE}/personality-profile/questions`),
        axios.get(`${API_BASE}/personality-profile/status/${user.id}`),
      ]);
      setQuestions(questionsRes.data.questions || []);
      if (statusRes.data.completed) {
        setCompleted(true);
        setProfile({
          scores: statusRes.data.scores,
          ...statusRes.data.personality_type,
        });
      }
    } catch (error) {
      console.error('Failed to fetch personality data:', error);
    } finally {
      setLoading(false);
    }
  }, [user.id]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleQuickComplete = async () => {
    setQuickRunning(true);
    try {
      const res = await axios.post(`${API_BASE}/demo/quick-complete-personality/${user.id}`);
      toast.success('Personality Profile auto-completed!');
      setCompleted(true);
      setProfile(res.data.profile);
    } catch (error) {
      toast.error('Quick complete failed');
    } finally {
      setQuickRunning(false);
    }
  };

  const handleAnswer = (likeMe) => {
    const q = questions[currentIdx];
    const newAnswers = { ...answers, [q.id]: likeMe };
    setAnswers(newAnswers);
    setImgLoaded(false);

    if (currentIdx < questions.length - 1) {
      setCurrentIdx(currentIdx + 1);
    } else {
      submitProfile(newAnswers);
    }
  };

  const submitProfile = async (finalAnswers) => {
    setSubmitting(true);
    try {
      const answersArray = Object.entries(finalAnswers).map(([qid, likeMe]) => ({
        question_id: qid,
        like_me: likeMe,
      }));
      const res = await axios.post(`${API_BASE}/personality-profile/submit`, {
        user_id: user.id,
        answers: answersArray,
      });
      toast.success('Personality Profile complete!');
      setCompleted(true);
      setProfile(res.data.profile);
    } catch (error) {
      toast.error('Failed to submit profile');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="p-12 flex items-center justify-center"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#0071e3]" /></div>;
  }

  // =========== RESULTS VIEW (matches document format) ===========
  if (completed && profile) {
    const dimensionOrder = ['conscientiousness', 'openness', 'stability', 'agreeableness', 'extraversion'];
    return (
      <div className="p-4 sm:p-8 md:p-12">
        <div className="max-w-4xl mx-auto">
          <div className="mb-8">
            <h1 className="text-4xl sm:text-5xl font-semibold text-[#1d1d1f] mb-3" data-testid="personality-complete-title">
              Personality Profile
            </h1>
          </div>

          {/* Personality Type Card */}
          <div className="bg-white rounded-2xl border border-[#d2d2d7] p-6 sm:p-8 mb-6" data-testid="personality-type-card">
            <div className="mb-4">
              <h2 className="text-xs font-semibold text-[#86868b] uppercase tracking-wider mb-1">Type</h2>
              <h3 className="text-2xl sm:text-3xl font-bold text-[#1d1d1f]" data-testid="personality-type-name">{profile.type_name}</h3>
              <p className="text-base text-[#86868b] mt-1">{profile.keywords}</p>
            </div>
            <p className="text-sm text-[#424245] leading-relaxed" data-testid="personality-description">
              {profile.description}
            </p>
          </div>

          {/* Big Five Scores — matching document format */}
          <div className="bg-white rounded-2xl border border-[#d2d2d7] p-6 sm:p-8 mb-6">
            <h2 className="text-lg font-semibold text-[#1d1d1f] mb-5">Big Five Dimension Scores</h2>
            <div className="space-y-4">
              {dimensionOrder.map(dim => {
                const data = profile.scores?.[dim];
                if (!data) return null;
                const score = data.score || 5;
                const color = DIMENSION_COLORS[dim] || '#0071e3';
                return (
                  <div key={dim} data-testid={`score-${dim}`}>
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-sm font-medium text-[#1d1d1f]">{data.label}</span>
                      <span className="text-sm font-bold" style={{ color }}>{score} / 10</span>
                    </div>
                    <div className="w-full h-3 bg-[#f5f5f7] rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-700"
                        style={{ width: `${score * 10}%`, backgroundColor: color }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Continue */}
          <div className="bg-green-50 rounded-2xl border border-green-200 p-6 text-center">
            <CheckCircle className="w-8 h-8 text-green-600 mx-auto mb-2" />
            <h3 className="text-xl font-semibold text-[#1d1d1f] mb-1">Profile Complete</h3>
            <p className="text-sm text-[#86868b] mb-4">Your personality profile has been saved. Continue to Career Preferences.</p>
            <Button
              onClick={() => navigate('/apprentice/career-preferences')}
              className="bg-[#0071e3] hover:bg-[#0077ed] text-white rounded-full px-8"
              data-testid="continue-to-discovery-btn"
            >
              Continue to Career Preferences <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // =========== PICOCEAN TEST VIEW ===========
  if (submitting) {
    return (
      <div className="p-12 flex flex-col items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#0071e3] mb-4" />
        <p className="text-lg text-[#86868b]">Analysing your personality...</p>
      </div>
    );
  }

  const q = questions[currentIdx];
  if (!q) return null;
  const totalQ = questions.length;
  const answeredCount = Object.keys(answers).length;

  return (
    <div className="p-4 sm:p-8 md:p-12">
      <div className="max-w-2xl mx-auto">
        {/* Quick Complete Banner */}
        <div className="bg-[#FFF8E1] border border-[#FFE082] rounded-2xl p-4 mb-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3" data-testid="personality-demo-banner">
          <div className="text-sm text-[#1d1d1f]">
            <span className="font-semibold">Investor Demo:</span> Skip to see completed personality profile
          </div>
          <Button
            onClick={handleQuickComplete}
            disabled={quickRunning}
            className="bg-[#FF9500] hover:bg-[#E68600] text-white rounded-full px-5 text-sm"
            data-testid="quick-complete-personality-btn"
          >
            <Zap className="w-3.5 h-3.5 mr-1.5" />
            {quickRunning ? 'Completing...' : 'Quick Complete'}
          </Button>
        </div>

        {/* Header */}
        <div className="mb-6 text-center">
          <h1 className="text-3xl sm:text-4xl font-semibold text-[#1d1d1f] mb-2">Visual Personality Profile</h1>
          <p className="text-base text-[#86868b]">Look at each image. Does this feel like you?</p>
        </div>

        {/* Progress */}
        <div className="mb-6">
          <div className="flex justify-between text-sm text-[#86868b] mb-2">
            <span>Question {currentIdx + 1} of {totalQ}</span>
            <span>{answeredCount} answered</span>
          </div>
          <div className="w-full h-1.5 bg-[#f5f5f7] rounded-full overflow-hidden">
            <div className="h-full rounded-full bg-[#0071e3] transition-all duration-500" style={{ width: `${((currentIdx + 1) / totalQ) * 100}%` }} />
          </div>
        </div>

        {/* Image Card */}
        <div className="bg-white rounded-2xl border border-[#d2d2d7] overflow-hidden mb-6" data-testid="picocean-card">
          <div className="relative w-full" style={{ paddingBottom: '66%' }}>
            {!imgLoaded && (
              <div className="absolute inset-0 bg-[#f5f5f7] flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0071e3]" />
              </div>
            )}
            <img
              src={q.image}
              alt={q.scenario}
              className="absolute inset-0 w-full h-full object-cover"
              onLoad={() => setImgLoaded(true)}
              style={{ display: imgLoaded ? 'block' : 'none' }}
            />
          </div>
          <div className="p-5 text-center">
            <p className="text-lg font-semibold text-[#1d1d1f]" data-testid="picocean-scenario">{q.scenario}</p>
          </div>
        </div>

        {/* Like Me / Not Me Buttons */}
        <div className="flex gap-4">
          <button
            onClick={() => handleAnswer(false)}
            className="flex-1 flex flex-col items-center gap-2 p-4 rounded-2xl border-2 border-[#d2d2d7] bg-white hover:border-red-300 hover:bg-red-50 transition-all active:scale-95"
            data-testid="not-me-btn"
          >
            <ThumbsDown className="w-7 h-7 text-[#86868b]" />
            <span className="text-sm font-semibold text-[#86868b]">Not Me</span>
          </button>
          <button
            onClick={() => handleAnswer(true)}
            className="flex-1 flex flex-col items-center gap-2 p-4 rounded-2xl border-2 border-[#d2d2d7] bg-white hover:border-green-300 hover:bg-green-50 transition-all active:scale-95"
            data-testid="like-me-btn"
          >
            <ThumbsUp className="w-7 h-7 text-[#34C759]" />
            <span className="text-sm font-semibold text-[#34C759]">Like Me</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default PersonalityProfile;
